import { GroundTruthCounter, GroundTruths, useDialog } from "@app/shared";
import { useMemo, useState } from "react";
import { useFetch } from "../../../hooks";
import { CheckboxInput, DataInfoCard, Icons, LabeledText, LINK_BUTTON, SortableTable, SortableTableHeaderType } from "../../shared";
import { CallerPayloadLink } from "./CallerPayloadLink";

type GroundTruthDebugDialogProps = {
    jobId: string;
    /**
     * Where the detail lives. A trial run is not a job and sits in its own table, so it is read
     * through its own route — same shape, same superadmin gate, different source. Defaults to the
     * job route, which is every caller but the admin table's trial rows.
     */
    kind?: "job" | "trial";
};

type Detection = { x: number; y: number; width: number; height: number; class_name: string; confidence: number };
type TileRegion = { x: number; y: number; width: number; height: number };
// id is required by SortableTable (T extends IBaseModel); the class name is already unique here.
type ClassTally = { id: string; className: string; count: number; minConfidence: number; maxConfidence: number };

function parseJsonArray<T>(json: string | undefined): T[] {
    if (!json) return [];
    try {
        const parsed = JSON.parse(json);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

// Distinct hues per class, assigned by first appearance so the legend, the table and the boxes all
// agree. Deliberately a fixed list rather than generated: predictable colours make a composite
// easier to re-read across jobs.
const CLASS_COLORS = ["#22d3ee", "#f472b6", "#facc15", "#4ade80", "#a78bfa", "#fb923c", "#38bdf8", "#f87171"];

/**
 * Tile strokes cycle through these so overlapping neighbours stay tellable apart.
 *
 * Tiles overlap by TILE_OVERLAP, so no two share an edge: six tiles over a 2940x1067 composite draw
 * six distinct vertical and four distinct horizontal lines, which in one uniform colour reads as a
 * grid of ~15 cells and makes the "6 tiles" label look wrong. Alternating colour plus a corner
 * number is what makes six boxes legible as six.
 *
 * Deliberately a different set from CLASS_COLORS — tiles and detections are drawn together and must
 * not be mistakable for each other.
 */
const TILE_COLORS = ["#ffffff", "#fb923c", "#a78bfa"];

export const GroundTruthDebugDialog: React.FC<GroundTruthDebugDialogProps> = ({ jobId, kind = "job" }) => {
    const [debugInfo, error, helpers] = useFetch(
        () => (kind === "trial" ? GroundTruths.getTrialDebugInfo(jobId) : GroundTruths.getDebugInfo(jobId)),
        [jobId, kind]);
    const [showDetections, setShowDetections] = useState(true);
    const [showTiles, setShowTiles] = useState(false);
    const [hoveredTile, setHoveredTile] = useState<number | undefined>(undefined);

    const detections = useMemo(() => parseJsonArray<Detection>(debugInfo?.detectionsJson), [debugInfo?.detectionsJson]);
    const tileRegions = useMemo(() => parseJsonArray<TileRegion>(debugInfo?.tileRegionsJson), [debugInfo?.tileRegionsJson]);

    const colorByClass = useMemo(() => {
        const map = new Map<string, string>();
        detections.forEach(d => {
            if (!map.has(d.class_name)) map.set(d.class_name, CLASS_COLORS[map.size % CLASS_COLORS.length]);
        });
        return map;
    }, [detections]);

    /**
     * Which tile each detection is attributed to — by its centre, not by overlap. With a 25%
     * overlap most boxes intersect two tiles, so "inside" is ambiguous; the centre gives exactly
     * one answer per detection, and the sum across tiles therefore equals the total count.
     */
    const tileIndexByDetection = useMemo(() => {
        return detections.map(d => {
            const cx = d.x + d.width / 2;
            const cy = d.y + d.height / 2;
            return tileRegions.findIndex(t => cx >= t.x && cx < t.x + t.width && cy >= t.y && cy < t.y + t.height);
        });
    }, [detections, tileRegions]);

    const hoveredDetections = useMemo(() => {
        if (hoveredTile === undefined) return [];
        return detections
            .filter((_, i) => tileIndexByDetection[i] === hoveredTile)
            .sort((a, b) => b.confidence - a.confidence);
    }, [hoveredTile, detections, tileIndexByDetection]);

    // Tallied from the detections rather than fetched separately, so the breakdown can never
    // disagree with the boxes drawn on the composite or with the total.
    const classTallies = useMemo((): ClassTally[] => {
        const byClass = new Map<string, number[]>();
        detections.forEach(d => byClass.set(d.class_name, [...(byClass.get(d.class_name) ?? []), d.confidence]));
        return Array.from(byClass.entries())
            .map(([className, confidences]) => ({
                id: className,
                className,
                count: confidences.length,
                minConfidence: Math.min(...confidences),
                maxConfidence: Math.max(...confidences),
            }))
            .sort((a, b) => b.count - a.count || a.className.localeCompare(b.className));
    }, [detections]);

    const classColumns = useMemo((): SortableTableHeaderType<ClassTally>[] => [
        {
            dataKey: "className", header: "Classification", width: 160,
            renderFunc: (row) => (
                <span className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: colorByClass.get(row!.className) }} />
                    {row!.className.replace(/_/g, " ")}
                </span>
            ),
        },
        { dataKey: "count", header: "Count", width: 70 },
        {
            dataKey: "minConfidence", header: "Confidence", width: 120,
            renderFunc: (row) => `${(row!.minConfidence * 100).toFixed(0)}–${(row!.maxConfidence * 100).toFixed(0)}%`,
        },
    ], [colorByClass]);

    if (helpers.isLoading) return <p className="text-sm text-semi-bright-text">Loading…</p>;
    if (error || !debugInfo) return <p className="text-sm text-semi-bright-text">Could not load the detail for this job.</p>;

    const { compositeImageUrl, sourceImageUrls, compositeWidth, compositeHeight, stitchMs, inferenceMs, photoCount, totalCount,
        requestDate, completedDate, counter, counterModel, notes, errorMessage, seamsJson } = debugInfo;
    const hasDimensions = !!compositeWidth && !!compositeHeight;

    // What this counter actually produced, and so what may be drawn.
    //
    // Both kinds of counter now return boxes: the detector's are measured, an LLM's are its own
    // estimate of where each vehicle it listed sits. Only the detector tiles, though — an LLM sees
    // the composite once — so inference tiles stay gated on it. Gated on the counter and not only
    // on the data because a row can hold tiles it did not produce; see the note below.
    const isLocalDetector = counter === GroundTruthCounter.OmniVision
        || counter === GroundTruthCounter.Default;
    const hasBoxes = detections.some(d => d.width > 0 && d.height > 0);
    const hasTiles = isLocalDetector && tileRegions.length > 0;
    const canOverlay = hasDimensions && (hasBoxes || hasTiles);

    // An LLM places its boxes by eye, in fractions of the image, and is not trying to be precise to
    // the pixel. Said plainly so a box that sits slightly off a vehicle reads as the approximation
    // it is rather than as a counter that mislocated something.
    const boxesAreEstimates = !isLocalDetector && hasBoxes;

    // Tiles stored against a counter that never tiles: they came from the stitching service, not
    // from whatever produced the count. Surfaced rather than silently dropped.
    const hasOrphanedTiles = !isLocalDetector && tileRegions.length > 0;

    const strokeWidth = hasDimensions ? Math.max(2, compositeWidth! / 600) : 2;
    const labelSize = hasDimensions ? Math.max(16, compositeWidth! / 55) : 16;
    const totalMs = (stitchMs ?? 0) + (inferenceMs ?? 0);

    // Wall clock from the batch arriving to counting finishing. Undefined while a job is still
    // queued or running, where there is no end to measure to yet.
    const elapsedMs = requestDate && completedDate
        ? new Date(completedDate).getTime() - new Date(requestDate).getTime()
        : undefined;

    // What the run spent not computing: storing the photos, then waiting for a worker. Shown
    // separately because "it took four minutes" has two very different causes and the model
    // timings alone cannot tell them apart. Floored at zero — the two timings come from different
    // clocks, so a fast run can land a few ms the wrong side of the subtraction.
    const overheadMs = elapsedMs != null ? Math.max(0, elapsedMs - totalMs) : undefined;
    const seconds = (ms: number) => `${(ms / 1000).toFixed(1)}s`;
    const tileColor = (index: number) => TILE_COLORS[index % TILE_COLORS.length];
    const isDimmed = (index: number) => hoveredTile !== undefined && hoveredTile !== index;

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-wrap gap-3">
                <DataInfoCard Icon={Icons.Truck} title="Vehicles">{totalCount ?? "—"}</DataInfoCard>
                <DataInfoCard Icon={Icons.Photo} title="Photos">{photoCount}</DataInfoCard>
                <DataInfoCard Icon={Icons.Eye} title="Counter">
                    {/* Default is not a counter — it is "whatever the service was configured to
                        use", and a completed run replaces it with what actually answered. It only
                        survives here when the service named a counter this build has no member for,
                        where printing "Default" would state something untrue about the run. */}
                    {counter === GroundTruthCounter.Default ? "—" : GroundTruthCounter[counter]}
                    {/* Under the name rather than beside it, like the runtime card's end-to-end
                        figure: this says which build of that counter answered — a fingerprint of the
                        weights the local detector loaded, or the model name for an LLM counter —
                        which is a detail of the line above, not a separate fact. Reported by the run
                        itself, so it cannot drift from what produced the number. */}
                    <span className="block text-xs font-normal normal-case text-gray-500 break-all">
                        {counterModel ?? "model not recorded"}
                    </span>
                </DataInfoCard>
                <DataInfoCard Icon={Icons.Clock} title="Runtime">
                    {totalMs > 0 ? seconds(totalMs) : "—"}
                    {/* Under the headline number rather than beside it as a second card: this is
                        the same measurement at a wider boundary, not an independent statistic. */}
                    {elapsedMs != null &&
                        <span className="block text-xs font-normal normal-case text-gray-500">
                            {seconds(elapsedMs)} end to end
                        </span>}
                </DataInfoCard>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <LabeledText label="Stitch" inline>{stitchMs != null ? seconds(stitchMs) : "—"}</LabeledText>
                <LabeledText label="Inference" inline>{inferenceMs != null ? seconds(inferenceMs) : "—"}</LabeledText>
                <LabeledText label="Upload + queue" inline>{overheadMs != null ? seconds(overheadMs) : "—"}</LabeledText>
                <LabeledText label="Composite" inline>{hasDimensions ? `${compositeWidth} × ${compositeHeight}` : "—"}</LabeledText>
                {hasTiles && <LabeledText label="Tiles" inline>{tileRegions.length}</LabeledText>}
            </div>

            {compositeImageUrl ?
                <>
                    {canOverlay ?
                        <div className="flex flex-wrap items-center gap-4">
                            {hasBoxes &&
                                <CheckboxInput label={`Detections (${detections.length})`} checked={showDetections} onChange={() => setShowDetections(!showDetections)} />
                            }
                            {hasTiles &&
                                <CheckboxInput label={`Inference tiles (${tileRegions.length})`} checked={showTiles} onChange={() => setShowTiles(!showTiles)} />
                            }
                            {showTiles &&
                                <span className="text-sm text-semi-bright-text">
                                    Tiles overlap by design — hover one to isolate it.
                                </span>
                            }
                            {boxesAreEstimates &&
                                <span className="text-sm text-semi-bright-text">
                                    Boxes are this model's own estimate of where each vehicle is, not
                                    measured positions — expect them to sit roughly, not exactly.
                                </span>
                            }
                            {hasOrphanedTiles &&
                                <span className="text-sm text-semi-bright-text">
                                    Inference tiles stored on this run came from the stitching service,
                                    not from this counter, and are not shown.
                                </span>
                            }
                        </div>
                        :
                        <p className="text-sm text-semi-bright-text">
                            This run stored no geometry, so there is nothing to draw over the
                            composite. The classifications below are what the counter returned.
                        </p>
                    }

                    <div className="relative w-full overflow-auto border border-separators-page">
                        {canOverlay ?
                            <svg
                                viewBox={`0 0 ${compositeWidth} ${compositeHeight}`}
                                className="w-full h-auto block"
                                role="img"
                                aria-label={`Stitched panorama with ${detections.length} detected vehicles outlined`}
                            >
                                <image href={compositeImageUrl} x={0} y={0} width={compositeWidth} height={compositeHeight} />

                                {showTiles && tileRegions.map((tile, i) => (
                                    <g key={`t${i}`} opacity={isDimmed(i) ? 0.12 : 1}>
                                        {/* Transparent fill, not none: it makes the whole tile area a hover target
                                            instead of just the 2px stroke. Dimmed tiles stop taking events so the
                                            one being hovered keeps them even where they overlap. */}
                                        <rect
                                            x={tile.x} y={tile.y} width={tile.width} height={tile.height}
                                            fill="transparent"
                                            stroke={tileColor(i)}
                                            strokeOpacity={0.85}
                                            strokeDasharray={strokeWidth * 4}
                                            strokeWidth={hoveredTile === i ? strokeWidth * 2.5 : strokeWidth}
                                            style={{ pointerEvents: isDimmed(i) ? "none" : "all", cursor: "pointer" }}
                                            onMouseEnter={() => setHoveredTile(i)}
                                            onMouseLeave={() => setHoveredTile(undefined)}
                                        />
                                        <rect
                                            x={tile.x} y={tile.y}
                                            width={labelSize * 1.5} height={labelSize * 1.4}
                                            fill={tileColor(i)}
                                            style={{ pointerEvents: "none" }}
                                        />
                                        <text
                                            x={tile.x + labelSize * 0.45} y={tile.y + labelSize}
                                            fill="#000000" fontSize={labelSize} fontWeight="700"
                                            style={{ pointerEvents: "none" }}
                                        >
                                            {i + 1}
                                        </text>
                                    </g>
                                ))}

                                {showDetections && detections.map((d, i) => (
                                    <g
                                        key={`d${i}`}
                                        // Detections fade with the tile they're attributed to, so isolating a tile
                                        // isolates its count too.
                                        opacity={showTiles && isDimmed(tileIndexByDetection[i]) ? 0.12 : 1}
                                        style={{ pointerEvents: "none" }}
                                    >
                                        <rect x={d.x} y={d.y} width={d.width} height={d.height}
                                            fill="none" stroke={colorByClass.get(d.class_name)} strokeWidth={strokeWidth} />
                                        <text
                                            x={d.x} y={Math.max(0, d.y - strokeWidth * 2)}
                                            fill={colorByClass.get(d.class_name)}
                                            fontSize={Math.max(11, compositeWidth! / 90)}
                                            fontWeight="600"
                                        >
                                            {`${d.class_name.replace(/_/g, " ")} ${(d.confidence * 100).toFixed(0)}%`}
                                        </text>
                                    </g>
                                ))}
                            </svg>
                            :
                            <img src={compositeImageUrl} alt="Stitched panorama" className="w-full h-auto block" />
                        }

                        {hoveredTile !== undefined &&
                            // Absolutely positioned inside the image wrapper: inserted into the
                            // column instead, it grew the dialog and shoved everything below it on
                            // every hover. pointer-events-none so it can never steal the hover from
                            // the tile underneath it and flicker.
                            <div className="absolute top-2 right-2 z-20 pointer-events-none max-w-xs max-h-[70%] overflow-hidden
                                            rounded shadow-lg border border-separators-page bg-background-page px-3 py-2 text-body-text">
                                <div className="flex items-center gap-2">
                                    <span className="inline-block w-3 h-3 rounded-sm border border-separators-page"
                                        style={{ backgroundColor: tileColor(hoveredTile) }} />
                                    <span className="font-semibold">Tile {hoveredTile + 1}</span>
                                    <span className="text-xs text-semi-bright-text">
                                        {hoveredDetections.length} {hoveredDetections.length === 1 ? "vehicle" : "vehicles"}
                                    </span>
                                </div>
                                <div className="text-xs text-semi-bright-text mb-1">
                                    {tileRegions[hoveredTile].width} × {tileRegions[hoveredTile].height} at
                                    {` (${tileRegions[hoveredTile].x}, ${tileRegions[hoveredTile].y})`}
                                </div>
                                {hoveredDetections.length === 0 ?
                                    <p className="text-xs text-semi-bright-text">
                                        Nothing attributed here — a vehicle visible in this tile counts toward
                                        whichever tile its centre falls in.
                                    </p>
                                    :
                                    <table className="text-xs">
                                        <tbody>
                                            {hoveredDetections.map((d, i) => (
                                                <tr key={i}>
                                                    <td className="pr-3 py-0.5">
                                                        <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle"
                                                            style={{ backgroundColor: colorByClass.get(d.class_name) }} />
                                                        {d.class_name.replace(/_/g, " ")}
                                                    </td>
                                                    <td className="py-0.5 text-right tabular-nums">{(d.confidence * 100).toFixed(0)}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                }
                            </div>
                        }
                    </div>

                    <a href={compositeImageUrl} target="_blank" rel="noreferrer"
                        className="text-sm text-link-page hover:text-link-page-hover underline w-fit">
                        Open full composite in a new tab
                    </a>
                </>
                :
                <p className="text-sm text-semi-bright-text">
                    No composite image was stored for this run{errorMessage ? " — it did not get that far." : "."}
                </p>
            }

            {/* A failed run has no composite, so without this the dialog opens on an absence and
                says nothing about why. The stitcher's refusals name the pair and the measurement
                that fell short, which is the most useful line in the view when a batch will not
                stitch — so it leads rather than sitting under the images it explains. */}
            {errorMessage &&
                <p className="text-sm text-red-600 whitespace-pre-line">{errorMessage}</p>}

            <SeamTable json={seamsJson} />

            {/* Open by default when there is no composite: on a failed stitch the photos are not
                the second question any more, they are the only one. */}
            <SourceImages urls={sourceImageUrls} photoCount={photoCount}
                defaultOpen={!compositeImageUrl} />

            {classTallies.length > 0 &&
                <SortableTable data={classTallies} columns={classColumns} initialSortKey="count" initialSortDir="desc" />
            }

            {notes &&
                // The counter's own caveats — what it excluded as off the lot, what it could not
                // resolve. The number above is only as trustworthy as these, so they sit with it
                // rather than being buried in the payload below.
                <p className="text-sm text-semi-bright-text whitespace-pre-line">{notes}</p>
            }

            <CallerPayloadLink jobId={jobId} />
        </div>
    );
};



type Seam = { a: number; b: number; inliers?: number; confidence?: number; overlap_px?: number; ncc?: number };

/**
 * What the stitcher measured for each pair of photos.
 *
 * The refusal message names one pair and one correlation. On its own that cannot distinguish the
 * two cases that matter: one weak seam in an otherwise sound batch, which is worth retrying, versus
 * a set of photographs that never overlapped enough, which needs different photographs. Seeing
 * every pair answers it at a glance — the weak one stands out, or it does not and they are all
 * marginal.
 *
 * Shown for successful stitches too. A composite that counted but only just is the one most likely
 * to be quietly wrong next time, and this is where that is visible before it fails.
 */
const SeamTable: React.FC<{ json: string | undefined }> = ({ json }) => {
    const seams = useMemo(() => parseJsonArray<Seam>(json), [json]);
    if (!seams.length) return null;

    // The thresholds the stitcher itself enforces, so the colouring marks exactly what it refuses
    // rather than a second opinion about what looks low.
    const MIN_INLIERS = 40;
    const MIN_NCC = 0.6;

    return (
        <div className="flex flex-col gap-1">
            <div className="text-sm font-semibold">Seams</div>
            <table className="text-sm w-fit">
                <thead className="text-semi-bright-text">
                    <tr>
                        <th className="text-left pr-6 font-normal">Photos</th>
                        <th className="text-right pr-6 font-normal">Matched points</th>
                        <th className="text-right pr-6 font-normal">Overlap</th>
                        <th className="text-right font-normal">Agreement</th>
                    </tr>
                </thead>
                <tbody className="tabular-nums">
                    {seams.map((seam) => {
                        const weakMatch = seam.inliers != null && seam.inliers < MIN_INLIERS;
                        const weakNcc = seam.ncc != null && seam.ncc < MIN_NCC;
                        return (
                            <tr key={`${seam.a}-${seam.b}`}>
                                <td className="pr-6">{seam.a} &amp; {seam.b}</td>
                                <td className={`text-right pr-6 ${weakMatch ? "text-red-600 font-semibold" : ""}`}>
                                    {seam.inliers ?? "—"}
                                </td>
                                <td className="text-right pr-6">
                                    {seam.overlap_px != null ? `${seam.overlap_px.toLocaleString()} px` : "—"}
                                </td>
                                <td className={`text-right ${weakNcc ? "text-red-600 font-semibold" : ""}`}>
                                    {seam.ncc != null ? seam.ncc.toFixed(2) : "—"}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <p className="text-xs text-semi-bright-text">
                Matched points below {MIN_INLIERS}, or agreement below {MIN_NCC.toFixed(2)}, is refused.
                A dash means the stitch stopped before that measurement was taken.
            </p>
        </div>
    );
};

type SourceImagesProps = {
    urls: string[] | undefined;
    photoCount: number;
    defaultOpen?: boolean;
};

/**
 * The photos as uploaded, in upload order, collapsed by default.
 *
 * A composite that came out wrong is almost always a question about its inputs — frames that did
 * not overlap enough, one from a different sweep, one shot into the sun — and none of that is
 * visible in the output. The stitcher's own failures are reported as a refusal with a reason, but
 * a stitch that *succeeds* badly says nothing at all, and this is the only way to see why.
 *
 * Collapsed because the composite is what you came to look at: the sources are the second question,
 * asked only when the first one looks wrong. Order is the order they were uploaded, which is the
 * order the stitcher saw them in — numbered, so a batch that stitched out of sequence reads
 * correctly here.
 */
const SourceImages: React.FC<SourceImagesProps> = ({ urls, photoCount, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    if (!urls?.length) {
        // Said rather than omitted: "there are no source photos" and "this dialog does not show
        // them" look identical as a blank space, and only one of them is worth investigating.
        return (
            <p className="text-sm text-semi-bright-text">
                {photoCount > 0
                    ? "The source photos for this run are no longer available."
                    : "No source photos were stored for this run."}
            </p>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <button
                type="button"
                className={`text-sm text-link-page hover:text-link-page-hover underline w-fit ${LINK_BUTTON}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                {isOpen ? "Hide" : "Show"} the {urls.length} source {urls.length === 1 ? "photo" : "photos"}
            </button>

            {isOpen &&
                <>
                    <p className="text-sm text-semi-bright-text">
                        As uploaded, in the order the stitcher received them. Click one to open it full size.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        {urls.map((url, i) => (
                            <a
                                key={url}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="relative block border border-separators-page rounded overflow-hidden
                                           hover:border-brand-primary"
                                title={`Photo ${i + 1} of ${urls.length}`}
                            >
                                <img src={url} alt={`Source photo ${i + 1}`}
                                    className="h-32 w-auto block" loading="lazy" />
                                {/* Numbered because order is the thing being checked — a batch that
                                    stitched in the wrong sequence is only visible against it. */}
                                <span className="absolute top-0 left-0 px-1.5 py-0.5 text-xs font-semibold
                                                 bg-black/60 text-white">
                                    {i + 1}
                                </span>
                            </a>
                        ))}
                    </div>
                </>
            }
        </div>
    );
};
